/**
 * API client for itinerary day regeneration and history management
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

/**
 * Regenerate a specific day with custom instructions
 * @param {Object} params
 * @param {string} params.itineraryId - Itinerary ID
 * @param {string} [params.interviewId] - Legacy alias for itineraryId
 * @param {string} params.versionId - Version ID (e.g., "v1")
 * @param {number} params.dayIndex - Zero-based day index
 * @param {string} params.instruction - User's regeneration instruction
 * @param {string} [params.editToken] - Edit capability token (optional for preview mode)
 * @param {string} [params.idempotencyKey] - Optional retry-safe key
 * @returns {Promise<{success: boolean, dayIndex: number, regenerationsRemaining: number, historyId: string, isPreviewMode: boolean}>}
 */
export async function regenerateDay({ itineraryId, interviewId, versionId, dayIndex, instruction, editToken, idempotencyKey }) {
  const resolvedId = itineraryId || interviewId;
  if (!resolvedId) throw new Error('Itinerary id is required');
  if (!versionId) throw new Error('Version id is required');
  const callable = httpsCallable(functions, 'regenerateDay');
  const result = await callable({
    itineraryId: resolvedId,
    versionId,
    dayIndex,
    instruction,
    ...(editToken ? { editToken } : {}),
    ...(idempotencyKey ? { idempotencyKey } : {})
  });
  return result.data;
}

/**
 * Get history entries for a specific day
 * @param {Object} params
 * @param {string} params.itineraryId
 * @param {string} [params.interviewId] - Legacy alias for itineraryId
 * @param {string} params.versionId
 * @param {number} params.dayIndex
 * @param {string} [params.editToken] - Edit capability token (optional for preview mode)
 * @param {number} [params.limit]
 * @returns {Promise<{success: boolean, itineraryId: string, versionId: string, dayIndex: number, entries: Array}>}
 */
export async function getDayHistory({ itineraryId, interviewId, versionId, dayIndex, editToken, limit }) {
  const resolvedId = itineraryId || interviewId;
  if (!resolvedId) throw new Error('Itinerary id is required');
  if (!versionId) throw new Error('Version id is required');
  const callable = httpsCallable(functions, 'getDayHistory');
  const result = await callable({
    itineraryId: resolvedId,
    versionId,
    dayIndex,
    ...(editToken ? { editToken } : {}),
    ...(typeof limit === 'number' ? { limit } : {})
  });
  return result.data;
}

/**
 * Revert a day to a previous history entry
 * @param {Object} params
 * @param {string} params.itineraryId
 * @param {string} [params.interviewId] - Legacy alias for itineraryId
 * @param {string} params.versionId
 * @param {number} params.dayIndex
 * @param {string} params.historyId - Which history entry to revert to
 * @param {string} [params.editToken] - Edit capability token (optional for preview mode)
 * @returns {Promise<{success: boolean, dayIndex: number, historyId: string, snapshotHistoryId: string, regenerationsRemaining: number, isPreviewMode: boolean}>}
 */
export async function revertDay({ itineraryId, interviewId, versionId, dayIndex, historyId, editToken }) {
  const resolvedId = itineraryId || interviewId;
  if (!resolvedId) throw new Error('Itinerary id is required');
  if (!versionId) throw new Error('Version id is required');
  const callable = httpsCallable(functions, 'revertDay');
  const result = await callable({
    itineraryId: resolvedId,
    versionId,
    dayIndex,
    historyId,
    ...(editToken ? { editToken } : {})
  });
  return result.data;
}

/**
 * Resume a failed full-itinerary generation from its first unfinished day.
 */
export async function retryFullItinerary({ itineraryId, interviewId, versionId, editToken }) {
  const resolvedId = itineraryId || interviewId;
  if (!resolvedId) throw new Error('Itinerary id is required');
  if (!versionId) throw new Error('Version id is required');
  const callable = httpsCallable(functions, 'retryFullItinerary');
  const result = await callable({
    itineraryId: resolvedId,
    versionId,
    ...(editToken ? { editToken } : {})
  });
  return result.data;
}
